/**
 * The public, read-only garage at `/g/:token` (§5, §11).
 *
 * Everything about this screen is shaped by one fact: **the visitor is not
 * signed in and may be anyone.** So:
 *
 *  - It renders outside `RequireAuth` and outside `GarageProvider`. It must work
 *    with no session at all, which is why the route sits above the guard in
 *    App.tsx rather than inside it.
 *  - There is not one write affordance on it — no "log service", no "mark
 *    checked", no add or edit anything. Not disabled: absent. There is no
 *    endpoint behind them to call.
 *  - It shows what `GET /public/:token` returns and nothing else. The response
 *    has no member list, no driver names, no odometer, no service history, no
 *    costs and no notes — not because they are filtered here, but because
 *    `PublicGarageResponse` has nowhere to put them (contracts/views.ts). This
 *    component could not leak them if it tried.
 *
 * A revoked link is a 404, and that is the honest answer: turning sharing off
 * destroys the token, so there is nothing at that URL any more (§11).
 */

import { Car, ChevronRight, Flag, Globe, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type { PublicGarageResponse } from '@svcdue/contracts';

import { usePublicGarage } from '../api/hooks';
import { ApiError } from '../api/client';
import { Empty, ErrorState, Loading } from '../components/states';
import { Eyebrow, Tally } from '../components/primitives';
import { Footer } from '../components/Footer';
import { groupBySystem } from '../lib/display';
import { daysUntil, fmtShortYear } from '../lib/format';

export function PublicGarage() {
  const { token } = useParams<{ token: string }>();
  const query = usePublicGarage(token ?? '');

  return (
    <div className="svcdue">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">
            <span className="bm-b bm-a" />
            <span className="bm-b bm-a" />
            <span className="bm-b bm-g" />
          </span>
          <div className="brand-txt">
            <b>SvcDue</b>
            <span>race maintenance</span>
          </div>
        </div>
        {/* Says plainly what this is: someone's garage, shared by link, read
            only. A visitor should never wonder whether they can edit it. */}
        <div className="public-badge">
          <Globe size={12} /> Public garage · read only
        </div>
      </header>

      <main className="main">
        {query.isPending && <Loading label="Loading garage…" />}
        {query.isError && <PublicError error={query.error} onRetry={() => void query.refetch()} />}
        {query.data && <PublicView data={query.data} />}
      </main>
      <Footer />
    </div>
  );
}

/**
 * A 404 here is the normal case, not a failure — the link was revoked, or was
 * never real. It deserves plain words rather than an error state with a retry
 * button that cannot help.
 */
function PublicError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.code === 'not_found') {
    return (
      <div className="screen">
        <Empty
          icon={<Globe size={20} />}
          title="This link isn't active"
          hint="The garage owner has stopped sharing it, or the link is incorrect."
        />
      </div>
    );
  }
  return (
    <div className="screen">
      <ErrorState error={error} onRetry={onRetry} />
    </div>
  );
}

export function PublicView({ data }: { data: PublicGarageResponse }) {
  const nextEvent = data.events[0];

  return (
    <div className="screen">
      <Eyebrow>Shared garage</Eyebrow>
      <h1 className="h1">{data.garageName}</h1>
      <p className="lede">
        A read-only view of this garage's fleet and what's coming up. Shared by link — no personal details are included.
      </p>

      {nextEvent && (
        <div className="card timeslip public-slip">
          <div className="ts-h">
            <span>Time slip</span>
            <span>Next event</span>
          </div>
          <div className="ts-days">
            <b>{daysUntil(nextEvent.date)}</b> days out
          </div>
          <div className="ts-rows">
            <div>
              <span>Event</span>
              <b>{nextEvent.name}</b>
            </div>
            {nextEvent.track && (
              <div>
                <span>Track</span>
                <b>{nextEvent.track}</b>
              </div>
            )}
            <div>
              <span>Running</span>
              <b>
                {data.cars.find((c) => c.id === nextEvent.carId)?.nickname ?? 'Car'} · est. {nextEvent.estPasses} passes
              </b>
            </div>
          </div>
          <div className="ts-perf" />
        </div>
      )}

      <div className="row-h">
        <h2 className="h2">Fleet</h2>
      </div>
      {data.cars.length === 0 ? (
        <Empty icon={<Car size={20} />} title="No cars shared yet" />
      ) : (
        <div className="cars-grid">
          {data.cars.map((car) => (
            /* A div, not a button: there is nowhere to navigate to. */
            <div className="car-card car-card-static" key={car.id}>
              <div className="car-top">
                <div className="car-ic">
                  {car.imageUrl ? <img src={car.imageUrl} alt="" className="car-thumb" /> : <Car size={18} />}
                </div>
                <div className="car-id">
                  <b>{car.nickname}</b>
                  <span>
                    {[car.year, car.make, car.model].filter(Boolean).join(' ') || 'No details'}
                    {car.motorCode ? ` · ${car.motorCode}` : ''}
                  </span>
                </div>
              </div>
              <Tally t={car.tally} />
              <div className="car-foot">
                <span>
                  <MapPin size={12} /> {car.useType === 'street' ? 'Street + Strip' : 'Strip only'}
                </span>
                {car.raceClass && <span>{car.raceClass}</span>}
              </div>
              {/* The build sheet, only when the owner opted in (§7). */}
              {data.showMaintenance && car.parts.length > 0 && <PublicParts parts={car.parts} />}
            </div>
          ))}
        </div>
      )}

      <div className="row-h">
        <h2 className="h2">Coming up</h2>
        <span className="row-sub">planned events &amp; forecast service</span>
      </div>
      <PublicTimeline data={data} />
    </div>
  );
}

/**
 * A car's parts on the public page — the build sheet, shown only when the owner
 * exposed maintenance detail. Read-only: name, part number, and a reorder link
 * where one is set. No status, no history — that stays with the members.
 */
function PublicParts({ parts }: { parts: PublicGarageResponse['cars'][number]['parts'] }) {
  const groups = groupBySystem(parts);
  // A build sheet runs long, so every system starts closed — the header row and
  // its count are the summary, and a visitor opens only what they came for.
  const [open, setOpen] = useState<Record<string, boolean>>({});

  return (
    <div className="public-parts">
      <div className="public-parts-h">Parts</div>
      {groups.map((group) => {
        const isOpen = open[group.system] ?? false;
        return (
          <div className="public-parts-group" key={group.system}>
            <button
              type="button"
              className="public-parts-group-h"
              aria-expanded={isOpen}
              onClick={() => setOpen((prev) => ({ ...prev, [group.system]: !isOpen }))}
            >
              <ChevronRight size={12} className={isOpen ? 'public-parts-chev open' : 'public-parts-chev'} />
              <span>{group.system}</span>
              <span className="public-parts-count">{group.items.length}</span>
            </button>
            {isOpen && (
              <div className="public-parts-body">
                {group.items.map((p, i) => (
                  <div className="public-part" key={`${p.name}-${i}`}>
                    <div className="public-part-id">
                      <b>{p.name}</b>
                      {p.partNumber && <span>{p.partNumber}</span>}
                    </div>
                    {p.buyUrl && (
                      <a className="public-part-buy" href={p.buyUrl} target="_blank" rel="noreferrer">
                        Reorder
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PublicTimeline({ data }: { data: PublicGarageResponse }) {
  const rows = [
    ...data.events.map((e) => ({ date: e.date, label: e.name, who: e.track ?? '', kind: 'event' as const })),
    ...data.occurrences.map((o) => ({ date: o.date, label: o.itemName, who: o.who, kind: 'proj' as const })),
    ...data.certs.map((c) => ({ date: c.date, label: c.itemName, who: c.who, kind: 'cert' as const })),
  ].sort((a, b) => (a.date < b.date ? -1 : 1));

  if (rows.length === 0) return <Empty icon={<Flag size={20} />} title="Nothing scheduled" />;

  return (
    <div className="mini-tl">
      {rows.map((r, i) => (
        <div className="mtl-row" key={`${r.kind}-${r.date}-${i}`}>
          <span className="mtl-date mtl-date-y">{fmtShortYear(r.date)}</span>
          <span className={'mtl-tick ' + (r.kind === 'cert' ? 'tick-cert' : r.kind === 'event' ? 'tick-event' : 'tick-proj')} />
          <span className="mtl-item">{r.label}</span>
          <span className="mtl-who">{r.who}</span>
        </div>
      ))}
    </div>
  );
}
